import {
  CreatePaymentFromPaymentRequestsDocument,
  PaymentsDataCollectionDocument,
  PaymentsPageDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import formatCurrency from 'modules/core/lib/formatCurrency'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import type { SelectAllFilters } from 'modules/banking/components/PayPaymentRequestsInBulkModal'

// Minimal type for payment request data needed by the form
interface PaymentRequestForBulkPayment {
  id: string
  amountCents: number
  currency: string
  dueDate?: string | null
}

export interface PaymentFromPaymentRequestsFormValues {
  label: string
  description?: string
  bankAccountId: string
  paymentRequestIds?: string[]
}

export const useCreatePaymentFromPaymentRequestsForm = ({
  onSuccess,
  paymentRequests = [],
  selectAll,
  excludedPaymentRequestIds,
  filters,
  selectedCount,
}: {
  onSuccess: (id: string) => void
  paymentRequests?: PaymentRequestForBulkPayment[]
  selectAll?: boolean
  excludedPaymentRequestIds?: string[]
  filters?: SelectAllFilters
  selectedCount?: number
}) => {
  const paymentRequestIds = paymentRequests.map((pr) => pr.id)
  const today = moment().format('YYYY-MM-DD')
  const weekStart = moment().startOf('week').format('YYYY-MM-DD')
  const weekEnd = moment().endOf('week').format('YYYY-MM-DD')
  const { company, employee } = useCurrentSession()
  const [createBankingPayment, { loading: loadingCreation }] = useMutation(
    CreatePaymentFromPaymentRequestsDocument,
    {
      extractorFn: (data) => data.bankingMutations.createBankingPayment,
      refetchQueries: [
        PaymentsDataCollectionDocument,
        {
          query: PaymentsPageDocument,
          variables: {
            companyId: company.id,
            featureNames: ['dev_finance_swan_reactivation'],
            todayDate: today,
            weekRange: { from: weekStart, to: weekEnd },
          },
        },
      ],
      awaitRefetchQueries: true,
    }
  )

  const defaultLabel = i18n.t('banking.bulk_payment.forms.default_name', {
    date: moment().format('D MMM YYYY'),
  })

  const defaultDescription = (() => {
    if (selectAll && selectedCount) {
      const activeFilters: string[] = []
      if (filters?.requesterIds?.length) {
        activeFilters.push(i18n.t('banking.paymentRequests.filters.requester'))
      }
      if (filters?.sourceTypes?.length) {
        activeFilters.push(i18n.t('banking.paymentRequests.filters.source'))
      }
      if (filters?.search) {
        activeFilters.push(i18n.t('banking.paymentRequests.filters.search'))
      }
      if (filters?.onTime || filters?.dueThisWeek || filters?.overdue) {
        activeFilters.push(i18n.t('banking.paymentRequests.filters.dueDateStatus'))
      }

      const countText = i18n.t('banking.bulk_payment.forms.default_description_count', {
        count: selectedCount,
      })

      if (activeFilters.length > 0) {
        return `${countText} · ${i18n.t('banking.bulk_payment.forms.default_description_filtered', { filters: activeFilters.join(', ') })}`
      }
      return countText
    }

    if (paymentRequests.length === 0) return ''

    const totalAmountCents = paymentRequests.reduce((sum, pr) => sum + pr.amountCents, 0)
    const currency = paymentRequests[0]?.currency || 'EUR'
    const formattedAmount = formatCurrency(totalAmountCents, currency)

    const dueDates = paymentRequests
      .map((pr) => pr.dueDate)
      .filter((date): date is string => date !== null && date !== undefined)
      .sort()
    const earliestDueDate = dueDates[0]
    const formattedDueDate = earliestDueDate ? moment(earliestDueDate).format('D MMM YYYY') : null

    if (formattedDueDate) {
      return i18n.t('banking.bulk_payment.forms.default_description_full', {
        count: paymentRequests.length,
        amount: formattedAmount,
        dueDate: formattedDueDate,
      })
    }
    return i18n.t('banking.bulk_payment.forms.default_description_amount', {
      count: paymentRequests.length,
      amount: formattedAmount,
    })
  })()

  const creationForm = useForm<PaymentFromPaymentRequestsFormValues>({
    defaultValues: {
      label: defaultLabel,
      bankAccountId: '',
      description: defaultDescription,
      paymentRequestIds: paymentRequestIds || [],
    },
    validateOnChange: true,
    validate: (values) => {
      const errors: Record<string, string> = {}
      if (!values.label) {
        errors.label = i18n.t('banking.bulk_payment.forms.validation_mandatory')
      } else if (values.label.trim().length <= 0) {
        errors.label = i18n.t('banking.bulk_payment.forms.validation_min_length', { length: 1 })
      } else if (values.label.length > 64) {
        errors.label = i18n.t('banking.bulk_payment.forms.validation_max_length', { length: 64 })
      }

      if (!values.bankAccountId) {
        errors.bankAccountId = i18n.t('banking.bulk_payment.forms.validation_mandatory')
      }
      return errors
    },
    onSubmit: async (values) => {
      const variables = {
        authorId: employee.id,
        label: values.label,
        description: values.description,
        bankAccountId: values.bankAccountId,
        paymentRequestIds: selectAll ? undefined : paymentRequestIds,
        selectAll: selectAll || undefined,
        excludedPaymentRequestIds: selectAll ? excludedPaymentRequestIds : undefined,
        requesterIds: selectAll ? filters?.requesterIds : undefined,
        sourceTypes: selectAll ? filters?.sourceTypes : undefined,
        search: selectAll ? filters?.search : undefined,
        onTime: selectAll ? filters?.onTime : undefined,
        dueThisWeek: selectAll ? filters?.dueThisWeek : undefined,
        overdue: selectAll ? filters?.overdue : undefined,
        dueDateFrom: selectAll ? filters?.dueDateFrom : undefined,
        dueDateTo: selectAll ? filters?.dueDateTo : undefined,
      }
      const result = await createBankingPayment({ variables })
      const handled = handleResult(result, (errors) => {
        creationForm.setErrors(errors)
      })
      if (handled.success) {
        const updatedPayment = handled.data.payment
        app.setFlash({
          type: 'correct',
          message: i18n.t('banking.bulk_payment.new_bulk_payment_modal.payment_created'),
        })
        updatedPayment && onSuccess(updatedPayment.id)
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_error'),
        })
      }
      return handled
    },
  })

  return {
    form: creationForm,
    loading: loadingCreation,
  }
}
